export * from "./types";
export * from "./costs";
export * from "./businessLogic";
export * from "./assessments";

import type { RIASSchemes, RIASScheme } from "./types";
import { CertificateState, RIASRecordType } from "./types";


export const isDev = process.env.NODE_ENV === "development";
export const isProd = process.env.NODE_ENV === "production";



const { default: raw } = await import("~/data/schemes.json");
const schemes = raw as RIASSchemes;


export const orderedJoin = (target: any, parts: string[], join: string) =>
	!target
		? ""
		: parts
				.map(key => target[key])
				.filter(value => value != null && String(value).trim() !== "")
				.join(join);

export const parseCertificateString = (input: string) => {
	// Split on first hyphen only
	const [idPart, suffixRaw = ""] = input.split(/-(.+)/);

	// Extract the C + digits, and also extract the digits alone
	const idMatch = idPart.match(/^C(\d+)$/i);

	const riasId = idMatch ? Number(idMatch[1]) : null;

	// Extract Stage / Amendment (case-insensitive, typo-flexible)
	const stageMatch = suffixRaw.match(/stage\s*(\d+)/i);
	const amendMatch = suffixRaw.match(/amend\w*\s*(\d+)/i);

	return {
		riasId, // numeric value
		stage: stageMatch ? Number(stageMatch[1]) : null,
		amendment: amendMatch ? Number(amendMatch[1]) : null,
	};
};

export const stageCount = (certificate: any): number =>
	certificate?.meta?.stages?.length ?? 0;

export const amendmentCount = (certificate: any): number =>
	certificate?.meta?.amendments?.length ?? 0;

// meta.isStagedCertificate is a boolean on the domestic wizard but a "Yes"/"No"
// string on the non-domestic one, so neither the flag nor the stages array can
// be trusted on its own to say whether a certificate is staged.
export const isStagedCertificate = (certificate: any): boolean =>
	stageCount(certificate) > 0 ||
	certificate?.meta?.isStagedCertificate === true ||
	certificate?.meta?.isStagedCertificate === "Yes";

export type CertificateFollowOns = { canAmend: boolean; canAddStage: boolean };

/**
 * Works out which certificates in a list may be followed on from.
 *
 * Every stage and amendment reuses the riasId of the certificate it follows on
 * from, so a riasId identifies one chain of records. Within a chain each stage
 * carries its own run of amendments, and a follow-on is always copied from the
 * newest record of a stage - its head. Stage 1 therefore stays amendable after
 * Stage 2 has been issued; only the newest stage may have a further stage added.
 */
export const certificateFollowOns = (
	certificates: any[],
): Map<string, CertificateFollowOns> => {
	const followOns = new Map<string, CertificateFollowOns>();
	const chains = new Map<string | number, any[]>();

	for (const certificate of certificates ?? []) {
		if (!certificate?.sk) continue;

		followOns.set(certificate.sk, { canAmend: false, canAddStage: false });

		const chainId = certificate.riasId ?? certificate.sk;
		const chain = chains.get(chainId);

		if (chain) chain.push(certificate);
		else chains.set(chainId, [certificate]);
	}

	for (const chain of chains.values()) {
		const heads = new Map<number, any>();

		for (const certificate of chain) {
			const stage = stageCount(certificate);
			const head = heads.get(stage);

			if (!head || amendmentCount(certificate) > amendmentCount(head)) {
				heads.set(stage, certificate);
			}
		}

		const newestStage = Math.max(...heads.keys());

		for (const [stage, head] of heads) {
			// Only a paid certificate can be followed on from, which also stops a
			// second follow-on being started while the first is still a draft.
			const paid = head.status === CertificateState.PAID;

			followOns.set(head.sk, {
				canAmend: paid,
				canAddStage: paid && stage === newestStage && isStagedCertificate(head),
			});
		}
	}

	return followOns;
};

export const schemeData = (schemeSlug: string): RIASScheme|null => {
	let schemeKey: keyof RIASSchemes|undefined = undefined;
	
	switch ( schemeSlug ) {
		case "s6Domestic":
		case "s6NonDomestic":
		case "s6NonDomesticAdv":
			schemeKey = schemeSlug as keyof RIASSchemes;
			break;
		
		case "section-6-domestic":
			schemeKey = "s6Domestic";
			break;
		case "section-6-non-domestic":
			schemeKey = "s6NonDomestic";
			break;
		case "section-6-non-domestic-advanced":
			schemeKey = "s6NonDomesticAdv";
			break;
		case "cpd-membership":
		case "cpd":
			schemeKey = "cpd";
			break;			
	}

	return schemeKey ? schemes[schemeKey] : null;
};

export const formattedTimestamp = (ts: number, format: any ={
		day: "2-digit",
		month: "short",
		year: "numeric",
} ) =>  ts == 0 ? "n/a" : new Intl.DateTimeFormat("en-GB", format).format(new Date(ts < 2525999000 ? ts * 1000 : ts));
	

export const ordinal = (n: number): string => {
	if (n == 0) return "0";

	const pr = new Intl.PluralRules("en", { type: "ordinal" });
	const suffixes = { one: "st", two: "nd", few: "rd", other: "th" };

	return n + suffixes[pr.select(n)];
};

const months = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export const formatLongDate = (timestamp: number) => {
	if (timestamp < 2525999000) timestamp *= 1000;

	const d = new Date(timestamp); // timestamp in ms
	const month = months[d.getMonth()];
	const dayOrdinal = ordinal(d.getDate());
	const year = d.getFullYear();
	return `${dayOrdinal} ${month}, ${year}`;
};

export const generateCodeVerifier = () => {
	const array = new Uint32Array(56);
	crypto.getRandomValues(array);
	return Array.from(array, dec => ("0" + dec.toString(16)).slice(-2)).join("");
};

export const generateCodeChallenge = async (verifier: string) => {
	const encoder = new TextEncoder();
	const data = encoder.encode(verifier);
	const digest = await crypto.subtle.digest("SHA-256", data);
	return btoa(String.fromCharCode(...new Uint8Array(digest)))
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
};

export const padId = (id: number, recordType: RIASRecordType | null = null) =>
	`${recordType ? recordType : ""}${recordType ? "#" : ""}${String(id).padStart(
		7,
		"0"
	)}`;

// Display form of an approved body id, e.g. RIA1-DB-0000012 - mirrors the
// certifier id (RIA1-D-0000001) built in generateSubscriptionName.
export const formatApprovedBodyId = (schemeSlug: string, id: string | number) => {
	const prefix = schemeData(schemeSlug)?.prefix ?? "";

	return `${prefix}-DB-${String(id).padStart(7, "0")}`;
};

// Trims every string in a payload and strips C0 control characters (a pasted
// tab in a warrant number once broke certificate PDFs). Newlines survive.
// Only plain objects and arrays are walked - binary data and sets pass through.
export const cleanStrings = <T,>(value: T): T => {
	if (typeof value === "string") {
		return value.replace(/[ -	-]/g, " ").trim() as T;
	}

	if (Array.isArray(value)) {
		return value.map(cleanStrings) as T;
	}

	if (value && typeof value === "object") {
		const proto = Object.getPrototypeOf(value);

		if (proto === Object.prototype || proto === null) {
			return Object.fromEntries(
				Object.entries(value).map(([k, v]) => [k, cleanStrings(v)]),
			) as T;
		}
	}

	return value;
};

export const toSlug = (s: string) =>
	s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

export const toCamel = (s: string) =>
	s.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

// --- helper parser ---
export const parsePlotRanges = (input, maxPlot = 9999) => {
	const errors = [];
	const result = new Set();

	const clean = input.replace(/[–—]/g, "-"); // normalize dash variants
	const tokens = clean
		.split(",")
		.map(t => t.trim())
		.filter(Boolean);

	for (const token of tokens) {
		const match = token.match(/^(\d+)(?:-(\d+))?$/);
		if (!match) {
			errors.push(`Invalid token "${token}"`);
			continue;
		}

		let [, start, end] = match.map(Number);
		if (!end) end = start;
		if (end < start) [start, end] = [end, start];

		for (let i = start; i <= end; i++) {
			if (i < 1 || i > maxPlot) {
				errors.push(`Plot ${i} out of bounds`);
				continue;
			}
			result.add(i);
		}
	}

	return { plots: Array.from(result).sort((a, b) => a - b), errors };
};

// --- convert plots array back to compact range string ---
export const plotsToRanges = plots => {
	if (!plots.length) return "";
	plots.sort((a, b) => a - b);
	const ranges = [];
	let start = plots[0];
	let prev = plots[0];
	for (let i = 1; i <= plots.length; i++) {
		if (plots[i] !== prev + 1) {
			ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
			start = plots[i];
		}
		prev = plots[i];
	}
	return ranges.join(", ");
};
